import { useCallback, useMemo, useState } from 'react';

import { useSearchState } from '@fly/shared-hooks';
import { PaginationState } from '@tanstack/react-table';

import {
  CursorPagination,
  CursorPaginationDirection,
  PaginationType,
  TableStateType,
} from '../TanstackTable.types';

export const PAGE_SIZE = 10;

const PAGINATION_DIRECTION_PARAM_KEY = 'cursor_direction';
const PAGINATION_CURSOR_PARAM_KEY = 'cursor';
export const serializeCursorPaginationState = (
  value: CursorPagination | null,
  params: URLSearchParams
) => {
  if (value && value?.cursor?.length > 0) {
    params.set(PAGINATION_DIRECTION_PARAM_KEY, value.cursor_direction);
    params.set(PAGINATION_CURSOR_PARAM_KEY, value.cursor);
  } else {
    params.delete(PAGINATION_DIRECTION_PARAM_KEY);
    params.delete(PAGINATION_CURSOR_PARAM_KEY);
  }

  return params;
};
const deserializeCursorPaginationState = (value: URLSearchParams) => {
  const direction = value.get(PAGINATION_DIRECTION_PARAM_KEY);
  const cursor = value.get(PAGINATION_CURSOR_PARAM_KEY);
  if (direction && cursor) {
    return {
      cursor_direction: direction as CursorPaginationDirection,
      cursor,
    };
  }
  return null;
};

export function useTableCursorPagination(stateType: TableStateType) {
  const [searchPaginationState, setSearchPaginationState] =
    useSearchState<CursorPagination | null>({
      initialValue: null,
      serialize: serializeCursorPaginationState,
      deserialize: deserializeCursorPaginationState,
    });
  const [statePagination, setStatePagination] =
    useState<CursorPagination | null>(null);

  const setPagination = useCallback(
    (pagination: CursorPagination) => {
      if (stateType === 'state') {
        setStatePagination(pagination);
        return;
      }

      setSearchPaginationState(pagination);
    },
    [stateType, setSearchPaginationState]
  );

  const resetPagination = useCallback(() => {
    if (stateType === 'state') {
      setStatePagination(null);
      return;
    }

    setSearchPaginationState(null);
  }, [setStatePagination, stateType, setSearchPaginationState]);

  const pagination = useMemo(() => {
    if (stateType === 'state') {
      return statePagination;
    }
    return searchPaginationState;
  }, [stateType, statePagination, searchPaginationState]);

  return {
    pagination,
    setPagination,
    resetPagination,
  };
}

const PAGINATION_PAGE_SIZE_PARAM_KEY = 'pageSize';
const PAGINATION_PAGE_INDEX_PARAM_KEY = 'pageIndex';
export const serializeOffsetPaginationState = (
  value: PaginationState | null,
  params: URLSearchParams
) => {
  if (value && value.pageSize && value.pageIndex) {
    params.set(PAGINATION_PAGE_SIZE_PARAM_KEY, value.pageSize.toString());
    params.set(PAGINATION_PAGE_INDEX_PARAM_KEY, value.pageIndex.toString());
  } else {
    params.delete(PAGINATION_PAGE_SIZE_PARAM_KEY);
    params.delete(PAGINATION_PAGE_INDEX_PARAM_KEY);
  }
  return params;
};
const deserializeOffsetPaginationState = (value: URLSearchParams) => {
  const pageSize = value.get(PAGINATION_PAGE_SIZE_PARAM_KEY);
  const pageIndex = value.get(PAGINATION_PAGE_INDEX_PARAM_KEY);
  if (pageSize && pageIndex) {
    return {
      pageSize: parseInt(pageSize),
      pageIndex: parseInt(pageIndex),
    };
  }
  return null;
};
export function useTableOffsetPagination(stateType: TableStateType) {
  const [statePagination, setStatePagination] =
    useState<PaginationState | null>(null);
  const [searchPaginationState, setSearchPaginationState] =
    useSearchState<PaginationState | null>({
      initialValue: null,
      serialize: serializeOffsetPaginationState,
      deserialize: deserializeOffsetPaginationState,
    });

  const setPagination = useCallback(
    (pagination: PaginationState) => {
      if (stateType === 'state') {
        setStatePagination(pagination);
        return;
      }

      setSearchPaginationState(pagination);
    },
    [stateType, setSearchPaginationState]
  );

  const resetPagination = useCallback(() => {
    if (stateType === 'state') {
      setStatePagination((prev) => (prev ? { ...prev, pageIndex: 1 } : null));
      return;
    }

    setSearchPaginationState({ pageIndex: 1, pageSize: PAGE_SIZE });
  }, [setStatePagination, stateType, setSearchPaginationState]);

  const pagination = useMemo(() => {
    let currentPagination: PaginationState | null = null;

    if (stateType === 'state') {
      currentPagination = statePagination;
    } else {
      currentPagination = {
        pageIndex: searchPaginationState?.pageIndex ?? 1,
        pageSize: searchPaginationState?.pageSize ?? PAGE_SIZE,
      };
    }

    return currentPagination || { pageIndex: 1, pageSize: PAGE_SIZE };
  }, [searchPaginationState, stateType, statePagination]);

  return {
    pagination,
    setPagination,
    resetPagination,
  };
}

export function useTablePaginationState(
  stateType: TableStateType,
  paginationType: PaginationType
): {
  pagination: CursorPagination | PaginationState | null;
  setPagination: (pagination: CursorPagination | PaginationState) => void;
  resetPagination: () => void;
} {
  const cursorPagination = useTableCursorPagination(stateType);
  const offsetPagination = useTableOffsetPagination(stateType);

  if (paginationType === 'cursor') {
    return {
      ...cursorPagination,
      setPagination: cursorPagination.setPagination as (
        pagination: CursorPagination | PaginationState
      ) => void,
    };
  }

  if (paginationType === 'offset') {
    return {
      ...offsetPagination,
      setPagination: offsetPagination.setPagination as (
        pagination: CursorPagination | PaginationState
      ) => void,
    };
  }

  if (paginationType === 'virtual_scroll') {
    return {
      pagination: null,
      setPagination: () => {},
      resetPagination: () => {},
    };
  }

  return {
    pagination: null,
    setPagination: () => {},
    resetPagination: () => {},
  };
}
